import apiClient from './api.service';

export type RiskLevel = 'Acceptable' | 'Moderate' | 'High';
export type RiskRating = 'Negligible' | 'Low' | 'Medium' | 'High' | 'Extremely High';
export type RiskDepartmentCode = 'QA' | 'QC' | 'PU' | 'SA' | 'MN' | 'ST' | 'HR' | 'OP';
export type RiskAssessmentScope = 'Company' | 'Project';

export interface RiskAssessment {
  id: string;
  company_id: string;
  scope: RiskAssessmentScope;
  project_id?: string | null;
  template_id?: string;
  assessment_number: string;
  assessment_date: string;
  assessed_by: string;
  assessment_team?: string[];
  overall_risk_level: RiskLevel;
  overall_risk_score?: number;
  status: 'Draft' | 'Under Review' | 'Approved' | 'Active' | 'Superseded';
  approved_by?: string;
  approval_date?: string;
  approval_notes?: string;
  review_date?: string;
  last_reviewed_date?: string;
  updated_by_name?: string;
  updated_by_designation?: string;
  register_last_updated_date?: string;
  created_by?: string;
  created_at: string;
  updated_at: string;
  is_deleted: boolean;
  project?: any;
  assessedBy?: any;
  risk_items?: RiskItem[];
}

export interface RiskItem {
  id: string;
  company_id: string;
  risk_assessment_id: string;
  department_code: RiskDepartmentCode;
  risk_number: string;
  project_id?: string | null;
  source_risk_item_id?: string | null;
  risk_category: string;
  risk_description: string;
  risk_cause?: string;
  risk_impact?: string;
  likelihood: RiskRating;
  impact: RiskRating;
  risk_score: number;
  risk_level: RiskLevel;
  existing_controls?: string;
  control_effectiveness: 'Effective' | 'Partially Effective' | 'Ineffective' | 'None';
  mitigation_actions?: string;
  mitigation_strategy?: string;
  responsible_person_id?: string;
  target_completion_date?: string;
  mitigation_cost: number;
  status: 'Open' | 'Mitigated' | 'Accepted' | 'Transferred' | 'Closed';
  mitigation_status: 'Not Started' | 'In Progress' | 'Completed' | 'On Hold';
  residual_likelihood?: RiskRating;
  residual_impact?: RiskRating;
  residual_risk_score?: number;
  residual_risk_level?: RiskLevel;
  contingency_required: boolean;
  contingency_plan?: string;
  contingency_responsibility?: string;
  internal_communication?: string;
  external_communication?: string;
  created_at: string;
  updated_at: string;
}

export const RISK_RATING_OPTIONS: RiskRating[] = [
  'Negligible',
  'Low',
  'Medium',
  'High',
  'Extremely High',
];

export const RISK_DEPT_OPTIONS: RiskDepartmentCode[] = ['QA', 'QC', 'PU', 'SA', 'MN', 'ST', 'HR', 'OP'];

export const RISK_CATEGORY_OPTIONS = ['Product Quality', 'Product Delivery', 'Other'];

/** apiClient already returns ApiResponse body `{ success, data, message }`. */
function unwrap<T>(response: { data?: T } | T): T {
  if (response && typeof response === 'object' && 'data' in (response as object)) {
    const data = (response as { data?: T }).data;
    if (data !== undefined) return data as T;
  }
  return response as T;
}

function unwrapList<T>(response: { data?: T[] } | T[]): T[] {
  const data = unwrap<T[] | { data?: T[] }>(response as any);
  if (Array.isArray(data)) return data;
  if (data && typeof data === 'object' && Array.isArray((data as any).data)) return (data as any).data;
  return [];
}

class RiskService {
  async getRiskAssessments(filters?: {
    project_id?: string;
    status?: string;
    scope?: RiskAssessmentScope;
  }): Promise<RiskAssessment[]> {
    const response = await apiClient.get('/risk/risk-assessments', { params: filters });
    return unwrapList<RiskAssessment>(response);
  }

  async getRiskAssessmentById(id: string): Promise<RiskAssessment> {
    const response = await apiClient.get(`/risk/risk-assessments/${id}`);
    return unwrap<RiskAssessment>(response);
  }

  async ensureCompanyRegister(): Promise<RiskAssessment> {
    const response = await apiClient.post('/risk/risk-assessments/ensure-company-register');
    return unwrap<RiskAssessment>(response);
  }

  async createRiskAssessment(data: Partial<RiskAssessment>): Promise<RiskAssessment> {
    const response = await apiClient.post('/risk/risk-assessments', data);
    return unwrap<RiskAssessment>(response);
  }

  async updateRiskAssessment(id: string, data: Partial<RiskAssessment>): Promise<RiskAssessment> {
    const response = await apiClient.put(`/risk/risk-assessments/${id}`, data);
    return unwrap<RiskAssessment>(response);
  }

  async approveRiskAssessment(id: string, notes?: string): Promise<RiskAssessment> {
    const response = await apiClient.post(`/risk/risk-assessments/${id}/approve`, { notes });
    return unwrap<RiskAssessment>(response);
  }

  async deleteRiskAssessment(id: string): Promise<void> {
    await apiClient.delete(`/risk/risk-assessments/${id}`);
  }

  async listItems(assessmentId: string): Promise<RiskItem[]> {
    const response = await apiClient.get(`/risk/risk-assessments/${assessmentId}/items`);
    return unwrapList<RiskItem>(response);
  }

  async createItem(assessmentId: string, data: Partial<RiskItem>): Promise<RiskItem> {
    const response = await apiClient.post(`/risk/risk-assessments/${assessmentId}/items`, data);
    return unwrap<RiskItem>(response);
  }

  async updateItem(assessmentId: string, itemId: string, data: Partial<RiskItem>): Promise<RiskItem> {
    const response = await apiClient.put(`/risk/risk-assessments/${assessmentId}/items/${itemId}`, data);
    return unwrap<RiskItem>(response);
  }

  async deleteItem(assessmentId: string, itemId: string): Promise<void> {
    await apiClient.delete(`/risk/risk-assessments/${assessmentId}/items/${itemId}`);
  }

  async spawnProjectSnapshot(
    assessmentId: string,
    data: { project_id: string; item_ids?: string[] }
  ): Promise<RiskAssessment> {
    const response = await apiClient.post(`/risk/risk-assessments/${assessmentId}/spawn-project-snapshot`, data);
    return unwrap<RiskAssessment>(response);
  }
}

export const riskService = new RiskService();
